import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import { catName } from '@/components/base/ArticleCard';
import { articles, siteName } from '@/mocks/articles';
import usePageMeta from '@/hooks/usePageMeta';
import JsonLd from '@/components/base/JsonLd';

function renderBlocks(articleId: string) {
  const a = articles.find((x) => x.id === articleId);
  if (!a) return null;
  return a.content.map((block, i) =>
    block.type === 'h3' ? (
      <h3 key={i} className="mt-8 font-heading text-xl font-bold leading-snug text-foreground-900 md:text-2xl">
        {block.text}
      </h3>
    ) : (
      <p key={i} className="mt-5 text-[15px] leading-8 text-foreground-700 md:text-base md:leading-8">
        {block.text}
      </p>
    )
  );
}

export default function NewsDetail() {
  const { id } = useParams();
  const article = useMemo(() => articles.find((a) => a.id === id), [id]);
  const [copied, setCopied] = useState(false);

  const related = useMemo(() => {
    if (!article) return [];
    const sameCat = articles.filter((a) => a.category === article.category && a.id !== article.id);
    const others = articles.filter((a) => a.category !== article.category);
    return [...sameCat, ...others].slice(0, 3);
  }, [article]);

  usePageMeta(article?.title, article?.summary);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-[60vh] flex-col items-center justify-center bg-background-50 px-4 text-center">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-folder-4-line text-2xl" aria-hidden="true" />
          </span>
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-900">没有找到这篇爆料</h1>
          <p className="mt-2 text-sm text-foreground-500">链接可能有误或内容已更新。</p>
          <Link to="/news" className="mt-6 inline-flex h-11 items-center gap-2 rounded-full bg-primary-500 px-6 text-sm font-semibold text-background-50 hover:bg-primary-600">
            <i className="ri-arrow-left-line" aria-hidden="true" />
            返回资讯中心
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const crumbs = [
    { label: '首页', to: '/' },
    { label: '最新爆料', to: '/news' },
    { label: catName(article.category), to: `/news?cat=${article.category}` },
    { label: article.title },
  ];

  const isRenders = article.category === 'renders';

  function handleCopy() {
    const url = window.location.href;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }

  return (
    <>
      <Navbar />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: article.title,
          description: article.summary,
          image: [article.image],
          datePublished: article.date,
          inLanguage: 'zh-CN',
          author: { '@type': 'Person', name: article.author },
          publisher: { '@type': 'Organization', name: siteName },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `https://agirx.cn/news/${article.id}` },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: crumbs.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: c.label,
            ...(c.to ? { item: `https://agirx.cn${c.to}` } : {}),
          })),
        }}
      />
      <main id="main" className="bg-background-50">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={crumbs} />

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-3">
            {/* 正文 */}
            <article className="lg:col-span-2">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Link
                  to={`/news?cat=${article.category}`}
                  className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
                    isRenders ? 'bg-accent-100 text-accent-800' : 'bg-primary-100 text-primary-800'
                  }`}
                >
                  <i className={isRenders ? 'ri-camera-line' : 'ri-fire-line'} aria-hidden="true" />
                  {catName(article.category)}
                </Link>
                {article.hot && (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800">
                    <i className="ri-hotspot-line" aria-hidden="true" />
                    热榜
                  </span>
                )}
                {article.pick && (
                  <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                    <i className="ri-vip-crown-2-line" aria-hidden="true" />
                    编辑精选
                  </span>
                )}
              </div>

              <h1 className="font-heading text-3xl font-black leading-snug tracking-tight text-foreground-950 md:text-4xl md:leading-snug">
                {article.title}
              </h1>
              <p className="mt-4 border-l-4 border-primary-500 pl-4 text-[15px] font-medium leading-7 text-foreground-600">
                {article.summary}
              </p>

              {/* 元信息 */}
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-y border-background-200 py-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-base font-bold text-background-50">
                    {article.author.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-900">{article.author}</p>
                    <p className="text-xs text-foreground-500">每日新料 编辑部</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1"><i className="ri-calendar-line" aria-hidden="true" />{article.date}</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-time-line" aria-hidden="true" />{article.readTime}</span>
                  <span className="inline-flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true" />{article.readCount} 阅读</span>
                </div>
              </div>

              <figure className="mt-7 overflow-hidden rounded-2xl border border-background-200">
                <img src={article.image} alt={article.imageAlt} className="w-full object-cover object-top" />
                <figcaption className="bg-background-100 px-4 py-2.5 text-xs text-foreground-500">
                  图片来源：网络流传/概念示意 · {article.imageAlt}
                </figcaption>
              </figure>

              {/* 提示条 */}
              <div className={`mt-6 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${isRenders ? 'border-accent-200 bg-accent-50 text-accent-900' : 'border-primary-200 bg-primary-50 text-primary-900'}`}>
                <i className={`mt-0.5 ${isRenders ? 'ri-camera-lens-line' : 'ri-error-warning-line'} text-lg`} aria-hidden="true" />
                <p>
                  <strong className="font-bold">重要提示：</strong>
                  {isRenders
                    ? '以下为网络流传的未发布产品图或概念渲染，可能与最终量产版存在差异，请以厂商官方信息为准。'
                    : '本文内容来自多方爆料，标注为“疑似/据传”的信息尚待官方确认，仅供参数曝光与价格预测参考。'}
                </p>
              </div>

              {/* 正文 */}
              <div className="mt-6">{renderBlocks(article.id)}</div>

              {/* 标签 */}
              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-sm font-semibold text-foreground-900">标签：</span>
                {article.tags.map((t) => (
                  <Link key={t} to={`/news?search=${encodeURIComponent(t)}`} className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200">
                    #{t}
                  </Link>
                ))}
              </div>

              {/* 分享与作者 */}
              <div className="mt-6 flex flex-col gap-4 rounded-2xl border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-lg text-background-50">
                    <i className="ri-flashlight-fill" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-heading text-sm font-bold text-foreground-900">觉得这条爆料有价值？</p>
                    <p className="text-xs text-foreground-500">分享给同样关注新机的朋友</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className={copied ? 'ri-check-line' : 'ri-link'} aria-hidden="true" />
                    {copied ? '链接已复制' : '复制链接'}
                  </button>
                </div>
              </div>
            </article>

            {/* 侧栏 */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
                  <h4 className="flex items-center gap-2 font-heading text-base font-black text-foreground-900">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                      <i className="ri-fire-line" aria-hidden="true" />
                    </span>
                    相关阅读
                  </h4>
                  <div className="mt-3 flex flex-col">
                    {related.map((a) => (
                      <Link key={a.id} to={`/news/${a.id}`} className="group flex items-start gap-3 border-b border-background-100 py-3 last:border-0">
                        <span className="h-14 w-16 shrink-0 overflow-hidden rounded-lg bg-background-100">
                          <img src={a.image} alt={a.imageAlt} className="h-full w-full object-cover object-top" />
                        </span>
                        <span className="flex flex-col">
                          <span className="font-heading text-sm font-semibold leading-snug text-foreground-800 transition-colors group-hover:text-primary-700 line-clamp-2">
                            {a.title}
                          </span>
                          <span className="mt-1 text-xs text-foreground-500">{a.date} · {catName(a.category)}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                <div className="mt-6 overflow-hidden rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-500 to-accent-600 p-6 text-background-50">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold">
                    <i className="ri-mail-send-line" aria-hidden="true" />
                    不想错过爆料？
                  </span>
                  <p className="mt-2 font-heading text-lg font-bold leading-snug">订阅每日电子报，第一时间收到新机消息。</p>
                  <Link to="/#subscribe" className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50">
                    前往订阅
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}